/**
 * 🎯 Hook useTechIcons - Correspondance des icônes de technologie
 * 
 * Hook React personnalisé qui associe les technologies à leurs icônes React Icons.
 * Sert de passerelle entre les données de la base de données et la représentation visuelle,
 * avec un système complet d'association d'icônes et de secours.
 * 
 * Fonctionnalités :
 * - 🎨 Intégration de React Icons pour des icônes de marque authentiques
 * - 🗄️ Intégration avec les données des technologies
 * - 🎯 Système de secours intelligent pour les technologies inconnues
 * - 🎨 Préservation des couleurs officielles des marques
 * - 📱 Chargement dynamique des icônes et mise en cache
 * - 🔧 Support de TypeScript avec une sécurité de typage complète
 */

import React from 'react';
import type { IconType } from 'react-icons';
import { useTechnologies, type Technology } from './useTechnologies';

// Imports d'icônes organisés par catégorie comme espace de noms pour le tree-shaking
import * as Si from 'react-icons/si';
import * as Fa from 'react-icons/fa';
import * as Md from 'react-icons/md';

/**
 * 🗂️ Configuration de la correspondance des icônes
 * 
 * Associe les noms d'icônes à leurs composants React Icons correspondants.
 * Cette correspondance est utilisée pour charger dynamiquement les icônes en fonction du
 * icon_name stocké dans la base de données.
 */
const ICON_MAPPING: Record<string, IconType> = {
  // Technologies Frontend
  'SiReact': Si.SiReact || Fa.FaCode,
  'SiTypescript': Si.SiTypescript || Fa.FaCode,
  'SiJavascript': Si.SiJavascript || Fa.FaCode,
  'SiHtml5': Si.SiHtml5 || Fa.FaCode,
  'SiCss3': Fa.FaCss3Alt || Fa.FaCode,
  'SiVite': Si.SiVite || Fa.FaCode,
  'SiSass': Si.SiSass || Fa.FaCode,
  'SiTailwindcss': Si.SiTailwindcss || Fa.FaCode,
  'SiAngular': Si.SiAngular || Fa.FaCode,
  'SiVuedotjs': Si.SiVuedotjs || Fa.FaCode,
  'SiSvelte': Si.SiSvelte || Fa.FaCode,
  'SiNextdotjs': Si.SiNextdotjs || Fa.FaCode,
  'SiNuxtdotjs': Si.SiNuxtdotjs || Fa.FaCode,

  // Technologies Backend
  'SiNodedotjs': Si.SiNodedotjs || Fa.FaCode,
  'SiExpress': Si.SiExpress || Fa.FaCode,
  'SiNestjs': Si.SiNestjs || Fa.FaCode,
  'SiPython': Si.SiPython || Fa.FaCode,
  'SiDjango': Si.SiDjango || Fa.FaCode,
  'SiFlask': Si.SiFlask || Fa.FaCode,
  'SiFastapi': Si.SiFastapi || Fa.FaCode,
  'SiPhp': Si.SiPhp || Fa.FaCode,
  'SiLaravel': Si.SiLaravel || Fa.FaCode,
  'SiRuby': Si.SiRuby || Fa.FaCode,
  'SiGo': Si.SiGo || Fa.FaCode,
  'SiRust': Si.SiRust || Fa.FaCode,
  'SiDotnet': Si.SiDotnet || Fa.FaCode,

  // Base de données & Hébergement
  'SiPostgresql': Si.SiPostgresql || Fa.FaCode,
  'SiMongodb': Si.SiMongodb || Fa.FaCode,
  'SiSupabase': Si.SiSupabase || Fa.FaCode,
  'SiMysql': Si.SiMysql || Fa.FaCode,
  'SiMariadb': Si.SiMariadb || Fa.FaCode,
  'SiRedis': Si.SiRedis || Fa.FaCode,
  'SiSqlite': Si.SiSqlite || Fa.FaCode,
  'SiFirebase': Si.SiFirebase || Fa.FaCode,
  'SiAmazon': Si.SiAmazon || Fa.FaCode,
  'SiGooglecloud': Si.SiGooglecloud || Fa.FaCode,

  // Outils de Développement
  'SiGit': Si.SiGit || Fa.FaCode,
  'SiGithub': Si.SiGithub || Fa.FaCode,
  'SiGitlab': Si.SiGitlab || Fa.FaCode,
  'SiDocker': Si.SiDocker || Fa.FaCode,
  'SiKubernetes': Si.SiKubernetes || Fa.FaCode,
  'SiFigma': Si.SiFigma || Fa.FaCode,
  'SiPostman': Si.SiPostman || Fa.FaCode,
  'SiInsomnia': Si.SiInsomnia || Fa.FaCode,
  'SiWebpack': Si.SiWebpack || Fa.FaCode,
  'SiEslint': Si.SiEslint || Fa.FaCode,
  'SiPrettier': Si.SiPrettier || Fa.FaCode,
  'SiJest': Si.SiJest || Fa.FaCode,
  'SiCypress': Si.SiCypress || Fa.FaCode,
  'SiStorybook': Si.SiStorybook || Fa.FaCode,

  // Science des Données & IA
  'SiTensorflow': Si.SiTensorflow || Fa.FaCode,
  'SiPytorch': Si.SiPytorch || Fa.FaCode,
  'SiScikitlearn': Si.SiScikitlearn || Fa.FaCode,
  'SiJupyter': Si.SiJupyter || Fa.FaCode,
  'SiPandas': Si.SiPandas || Fa.FaCode,
  'SiNumpy': Si.SiNumpy || Fa.FaCode,
  'SiApachespark': Si.SiApachespark || Fa.FaCode,
  'SiTableau': Si.SiTableau || Fa.FaCode,
  'SiR': Si.SiR || Fa.FaCode,
  'SiAnaconda': Si.SiAnaconda || Fa.FaCode,

  // Technologies Mobiles
  'SiFlutter': Si.SiFlutter || Fa.FaCode,
  'SiIonic': Si.SiIonic || Fa.FaCode,
  'SiReactos': Si.SiReactos || Fa.FaCode,

  // Icônes Génériques
  'FaCode': Fa.FaCode,
  'FaServer': Fa.FaServer,
  'FaDatabase': Fa.FaDatabase,
  'FaTools': Fa.FaTools,
  'FaChartBar': Fa.FaChartBar,
  'FaMobile': Fa.FaMobile,
  'FaCloud': Fa.FaCloud,
  'FaCog': Fa.FaCog,
  'FaShieldAlt': Fa.FaShieldAlt,
  'FaNetworkWired': Fa.FaNetworkWired,

  // Icônes Material Design
  'MdApi': Md.MdApi,
  'MdWeb': Md.MdWeb,
  'MdAnalytics': Md.MdAnalytics,
};

/**
 * 🎯 Icônes de secours par catégorie
 * 
 * Icônes par défaut pour chaque catégorie lorsqu'aucune icône de technologie
 * spécifique n'est trouvée ou disponible.
 */
const CATEGORY_FALLBACKS: Record<string, IconType> = {
  frontend: Md.MdWeb,
  backend: Fa.FaServer,
  database: Fa.FaDatabase,
  tools: Fa.FaTools,
  testing: Fa.FaCog,
  datascience: Fa.FaChartBar,
  api: Md.MdApi,
  mobile: Fa.FaMobile,
  design: Si.SiFigma || Fa.FaCode,
  other: Fa.FaCode,
};

/**
 * 🎯 Interface Technology avec icône
 * 
 * Étend l'interface Technology de base avec le composant d'icône résolu.
 * Fournit un objet technologie complet prêt pour le rendu.
 */
export interface TechnologyWithIcon extends Technology {
  icon: IconType;
  displayName: string;
}

/**
 * 🔧 Interface de retour du Hook
 * 
 * Définit le type de retour du hook useTechIcons.
 */
export interface UseTechIconsReturn {
  technologies: TechnologyWithIcon[];
  loading: boolean;
  error: string | null;
  categories: string[];
  getTechWithIcon: (name: string) => TechnologyWithIcon | undefined;
  getTechsByCategory: (category: string) => TechnologyWithIcon[];
  getIconByName: (iconName: string) => IconType;
  refreshTechnologies: () => Promise<void>;
}

/**
 * 🔧 Hook useTechIcons
 * 
 * Hook principal pour gérer les icônes de technologie.
 * Associe les données de technologie avec React Icons pour une solution de rendu complète.
 * 
 * @returns UseTechIconsReturn - Technologies avec icônes et fonctions utilitaires
 */
export const useTechIcons = (): UseTechIconsReturn => {
  // Utilisation du hook de base pour les technologies
  const {
    technologies: baseTechnologies,
    loading,
    error,
    categories,
    getTechByName,
    getTechsByCategory: getBaseTechsByCategory,
    getLocalizedName,
    refreshTechnologies
  } = useTechnologies();

  /**
   * 🎨 Récupère le composant d'icône par son nom
   * 
   * Résout le nom de l'icône en un composant React Icon avec un système de secours.
   * 
   * @param iconName - Nom de l'icône dans la base de données
   * @param category - Catégorie de la technologie pour le secours
   * @returns Composant d'icône
   */
  const getIconByName = (iconName: string, category?: string): IconType => {
    // Tentative de correspondance exacte en premier
    if (ICON_MAPPING[iconName]) {
      return ICON_MAPPING[iconName];
    }

    // Tentative de secours sur la catégorie
    if (category && CATEGORY_FALLBACKS[category.toLowerCase()]) {
      return CATEGORY_FALLBACKS[category.toLowerCase()];
    }

    // Secours par défaut
    return Fa.FaCode;
  };

  /**
   * 🔄 Transforme les technologies en y insérant les icônes
   * 
   * Convertit les technologies de base pour y inclure les composants d'icônes résolus
   * et les noms d'affichage localisés. Mémoïsé pour la performance.
   */
  const technologies = React.useMemo((): TechnologyWithIcon[] => {
    return baseTechnologies.map(tech => ({
      ...tech,
      icon: getIconByName(tech.icon_name, tech.category),
      displayName: getLocalizedName(tech)
    }));
  }, [baseTechnologies, getLocalizedName]);

  /**
   * 🔍 Récupère la technologie avec son icône par son nom
   * 
   * Trouve une technologie par son nom et la renvoie avec son icône résolue.
   * 
   * @param name - Nom de la technologie à rechercher
   * @returns Technologie avec icône ou undefined
   */
  const getTechWithIcon = (name: string): TechnologyWithIcon | undefined => {
    const baseTech = getTechByName(name);
    if (!baseTech) return undefined;

    return {
      ...baseTech,
      icon: getIconByName(baseTech.icon_name, baseTech.category),
      displayName: getLocalizedName(baseTech)
    };
  };

  /**
   * 📋 Récupère les technologies avec icônes par catégorie
   * 
   * Filtre les technologies par catégorie et inclut les icônes résolues.
   * 
   * @param category - Catégorie à filtrer
   * @returns Tableau de technologies avec icônes
   */
  const getTechsByCategory = (category: string): TechnologyWithIcon[] => {
    const baseTechs = getBaseTechsByCategory(category);
    return baseTechs.map(tech => ({
      ...tech,
      icon: getIconByName(tech.icon_name, tech.category),
      displayName: getLocalizedName(tech)
    }));
  };

  return {
    technologies,
    loading,
    error,
    categories,
    getTechWithIcon,
    getTechsByCategory,
    getIconByName: (iconName: string) => getIconByName(iconName),
    refreshTechnologies
  };
};

export default useTechIcons;
